import type { BindingContext } from '../../../utils/bindingContext';
import { Component, OnMounted, Param } from '@paperbits/common/ko/decorators';
import type { StyleModel } from '@paperbits/common/styles/styleModel';
import type { BackgroundModel } from '@paperbits/common/widgets/background';
import type { IExternalContentResolver } from '../../../cms/client/IExternalContentResolver';
import { format, isSameMonth, type Locale } from 'date-fns'; // TODO(date-fns)
import * as ko from 'knockout';
import * as i18nextko from 'i18next-ko';
import type { CmsContentContract } from '../../../cms/content/ko/CmsContentContract';
import type { CategoryLabel } from '../../../common/categories/categoryLabel';
import { CategoryLabels } from '../../../common/categories/categoryLabels';
import type { EventType } from '../../../eventTypes/eventType';
import { EventTypes } from '../../../eventTypes/eventTypes';
import type { DamMediaContract } from '../../../media/damMediaContract';
import type { HyperlinkModel } from '../../../permalinks/hyperlinkModel';
import { localeSwitcher } from '../../../translations/localeSwitcher';
import camelCaseToKebabCase from '../../../utils/camelCaseToKebabCase';
import { AspectRatio, type AspectRatioMap, emptyAspectRatioMap } from '../../../utils/imageTransform';
import { ButtonModel } from '../../button/buttonModel';
import { Button } from '../../button/ko/button';
import type { IBackgroundProvider } from '../bg/IBackgroundProvider';
import { widgetName } from '../constants';
import { ReplyCardModel, type ReplyCardModelInjectables } from '../replyCardModel';
import replyCardVideo from './reply-card-video-player.html';
import { EventDate, isSameDayFunction } from '../../../workshops/eventDate/eventDate';
import template from './reply-card.html';
import cardAwardDefault from './cards/card-award-default.html';
import cardHeroDefault from './cards/card-hero-default.html';
import cardHorizontalEditorial from './cards/card-horizontal-editorial.html';
import cardInfoBannerDefault from './cards/card-info-banner-default.html';
import cardInfoSlider11Default from './cards/card-info-slider-1-1-default.html';
import cardInfoSlider11Icon from './cards/card-info-slider-1-1-icon.html';
import cardInfoSlider11IconSmall from './cards/card-info-slider-1-1-icon-small.html';
import cardInfoSlider11Light from './cards/card-info-slider-1-1-light.html';
import cardInfoSlider23Default from './cards/card-info-slider-2-3-default.html';
import cardOfficeDefault from './cards/card-office-default.html';
import cardSerpDefault from './cards/card-serp-default.html';
import cardSliderDefault from './cards/card-slider-default.html';
import cardSliderVideoDefault from './cards/card-slider-video-default.html';
import cardWallAbstractCover from './cards/card-wall-abstract-cover.html';
import cardWallAbstractDefault from './cards/card-wall-abstract-default.html';
import cardWallBusinessImpacts from './cards/card-wall-business-impacts.html';
import cardWallEventAny from './cards/card-wall-event-any.html';
import cardWallHover11Default from './cards/card-wall-hover-1-1-default.html';
import cardWallHover169Default from './cards/card-wall-hover-16-9-default.html';
import cardWallHover23Default from './cards/card-wall-hover-2-3-default.html';
import cardWallNohover169Default from './cards/card-wall-nohover-16-9-default.html';
import cardWallProductDefault from './cards/card-wall-product-default.html';
import cardWallReleaseDefault from './cards/card-wall-release-default.html';
import cardWallThumb11Default from './cards/card-wall-thumb-1-1-default.html';
import cardWallThumb11DefaultVideoBackground from './cards/card-wall-thumb-1-1-default-video-background.html';
import cardWallThumb11Logo from './cards/card-wall-thumb-1-1-logo.html';
import cardWallThumb11LogoVideoBackground from './cards/card-wall-thumb-1-1-logo-video-background.html';
import cardWallThumb23Default from './cards/card-wall-thumb-2-3-default.html';
import cardWallThumb23DefaultVideoBackground from './cards/card-wall-thumb-2-3-default-video-background.html';
import cardWallThumb23Logo from './cards/card-wall-thumb-2-3-logo.html';
import cardWallThumb23LogoVideoBackground from './cards/card-wall-thumb-2-3-logo-video-background.html';
import cardWebsiteDefault from './cards/card-website-default.html';

export interface ReplyCardViewModelInjectables extends ReplyCardModelInjectables {
    backgroundProvider?: IBackgroundProvider;
    tamTamyCMSContentResolver?: IExternalContentResolver;
}

@Component({
    selector: widgetName,
    template: template,
    childTemplates: {
        'card-award-combined': cardAwardDefault, // it's the same as default
        'card-award-default': cardAwardDefault,
        'card-hero-default': cardHeroDefault,
        'card-horizontal-editorial': cardHorizontalEditorial,
        'card-info-banner-default': cardInfoBannerDefault,
        'card-info-slider-1-1-default': cardInfoSlider11Default,
        'card-info-slider-1-1-icon': cardInfoSlider11Icon,
        'card-info-slider-1-1-icon-small': cardInfoSlider11IconSmall,
        'card-info-slider-1-1-light': cardInfoSlider11Light,
        'card-info-slider-2-3-default': cardInfoSlider23Default,
        'card-office-default': cardOfficeDefault,
        'card-serp-default': cardSerpDefault,
        'card-slider-default': cardSliderDefault,
        'card-slider-video-default': cardSliderVideoDefault,
        'card-wall-abstract-cover': cardWallAbstractCover,
        'card-wall-abstract-default': cardWallAbstractDefault,
        'card-wall-business-impacts': cardWallBusinessImpacts,
        'card-wall-event-any': cardWallEventAny,
        'card-wall-hover-1-1-default': cardWallHover11Default,
        'card-wall-hover-16-9-default': cardWallHover169Default,
        'card-wall-hover-2-3-default': cardWallHover23Default,
        'card-wall-nohover-16-9-default': cardWallNohover169Default,
        'card-wall-product-default': cardWallProductDefault,
        'card-wall-release-default': cardWallReleaseDefault,
        'card-wall-thumb-1-1-default': cardWallThumb11Default,
        'card-wall-thumb-1-1-default-video-background': cardWallThumb11DefaultVideoBackground,
        'card-wall-thumb-1-1-logo': cardWallThumb11Logo,
        'card-wall-thumb-1-1-logo-video-background': cardWallThumb11LogoVideoBackground,
        'card-wall-thumb-2-3-default': cardWallThumb23Default,
        'card-wall-thumb-2-3-default-video-background': cardWallThumb23DefaultVideoBackground,
        'card-wall-thumb-2-3-logo': cardWallThumb23Logo,
        'card-wall-thumb-2-3-logo-video-background': cardWallThumb23LogoVideoBackground,
        'card-website-default': cardWebsiteDefault,
        replyCardVideo: replyCardVideo
    }
})
export class ReplyCardViewModel {

    @Param()
    public card: ReplyCardViewModel;

    public readonly templateName: ko.PureComputed<string>;

    public readonly locale: ko.Observable<Locale>;
    public readonly cmsContentKey: ko.Observable<string>;
    public readonly date: ko.Observable<string>;
    public readonly publicationDate: ko.Observable<number>;
    public readonly title: ko.Observable<string>;
    public readonly abstract: ko.Observable<string>;
    public readonly categoryKey: ko.Observable<string>;
    public readonly categoryLabel: ko.Computed<CategoryLabel>;
    public readonly tags: ko.ObservableArray<string>;
    public readonly hyperlink: ko.Observable<HyperlinkModel>;
    public readonly layout: ko.Observable<string>;
    public readonly applyOwnGrid: ko.Observable<boolean>;
    public readonly applyDefaultBg: ko.Observable<boolean>;
    public readonly mediaBackground: ko.Observable<BackgroundModel>;
    public readonly logoSourceUrl: ko.Observable<string>;
    public readonly logoIcon: ko.Observable<string>;
    public readonly logoCaption: ko.Observable<string>;
    public readonly coverSourceUrl: ko.Observable<AspectRatioMap>;
    public readonly styles: ko.Observable<StyleModel>;
    public readonly cta: ko.Observable<Button>;
    public readonly ctaAppearanceType: ko.Observable<string>;
    public readonly lowerDate: ko.Observable<EventDate>;
    public readonly upperDate: ko.Observable<EventDate>;
    public readonly isSameDay: ko.Computed<boolean>;
    public readonly isSameMonth: ko.Computed<boolean>;
    public readonly eventTypeKey: ko.Observable<string>;
    public readonly eventType: ko.Computed<EventType>;
    public readonly location: ko.Observable<string>;
    public readonly altDefaultBackgroundKey: ko.Observable<string>;
    public readonly attributes: ko.Computed<{ [key: string]: string }>;
    public readonly layoutClasses: ko.Computed<string>;
    public readonly shouldBeWrappedInsideAnchor: ko.Computed<boolean>;
    public readonly videoPlayer: ko.Observable<string>;
    public readonly videoPlayerHero: ko.Observable<string>;
    public readonly disableLazyLoadImage: ko.Observable<boolean>;
    public readonly id: ko.Observable<string>;
    public readonly language: ko.Observable<string>;
    public readonly downloadIcon: ko.Observable<boolean>;
    // public readonly unwrappedMediaBackgroundUrl: ko.Computed<string>; // TODO Remove after switching between background images and real img tags

    public readonly region?: ko.Observable<string>;
    public readonly country?: ko.Observable<string>;
    public readonly city?: ko.Observable<string>;
    public readonly mainOffice?: ko.Observable<boolean>;
    public readonly addressLine1?: ko.Observable<string>;
    public readonly addressLine2?: ko.Observable<string>;
    public readonly phone?: ko.Observable<string>;
    public readonly fax?: ko.Observable<string>;
    public readonly officeType: ko.Observable<string>;
    public readonly googleMapsURL: ko.Observable<string>;

    public readonly infoLine?: ko.Observable<string>;
    public readonly mainSites?: ko.ObservableArray<string>;
    public readonly siteLinks?: ko.Observable<string>;

    public coverWidth?: ko.Observable<number>;

    constructor() {
        this.id = ko.observable<string>();
        this.language = ko.observable<string>();
        this.locale = ko.observable<Locale>();
        this.cmsContentKey = ko.observable<string>();
        this.date = ko.observable<string>();
        this.publicationDate = ko.observable<number>();
        this.title = ko.observable<string>();
        this.abstract = ko.observable<string>();
        this.categoryKey = ko.observable<string>();
        this.categoryLabel = ko.computed<CategoryLabel>((): CategoryLabel => {
            const key: string = this.categoryKey();
            if (!key || key === CategoryLabels.getEmptyKey()) {
                return null;
            }
            const cat: CategoryLabel[] = CategoryLabels.get(key);
            return cat.length > 0 ? cat[0] : null;
        });
        this.tags = ko.observableArray<string>();
        this.styles = ko.observable<StyleModel>();
        this.hyperlink = ko.observable<HyperlinkModel>();
        this.layout = ko.observable<string>();
        this.applyOwnGrid  = ko.observable<boolean>();
        this.applyDefaultBg = ko.observable<boolean>();
        this.mediaBackground = ko.observable<BackgroundModel>();
        this.logoSourceUrl = ko.observable<string>();
        this.logoIcon = ko.observable<string>();
        this.logoCaption = ko.observable<string>();
        this.coverSourceUrl = ko.observable<AspectRatioMap>();
        this.cta = ko.observable<Button>();
        this.ctaAppearanceType = ko.observable<string>();
        this.lowerDate = ko.observable<EventDate>();
        this.upperDate = ko.observable<EventDate>();
        this.isSameDay = ko.computed<boolean>((): boolean => {
            const lower: EventDate = this.lowerDate();
            const upper: EventDate = this.upperDate();

            return !!(lower?.isValid() && upper?.isValid() && isSameDayFunction(lower, upper));
        });
        this.isSameMonth = ko.computed<boolean>((): boolean => {
            const lower: EventDate = this.lowerDate();
            const upper: EventDate = this.upperDate();
            return !!(lower?.isValid() && upper?.isValid() && isSameMonth(lower.timestamp, upper.timestamp));
        });
        this.eventTypeKey = ko.observable<string>();
        this.eventType = ko.computed<EventType>((): EventType => {
            const key: string = this.eventTypeKey();
            if (!key || key === EventTypes.getEmptyKey()) {
                return null;
            }
            const type: EventType[] = EventTypes.get(key);
            return type.length > 0 ? type[0] : null;
        });
        this.location = ko.observable<string>();
        this.altDefaultBackgroundKey = ko.observable<string>();
        this.attributes = ko.computed((): { [key: string]: string } =>
            this.hyperlink()?.target === '_blank' ? {
                rel: 'noreferrer',
                title: this.title()
            } : {title: this.title()});
        this.layoutClasses = ko.computed<string>((): string => {
            const layout: string = this.layout();
            return layout ? `${layout.substring(0, layout.lastIndexOf('-'))} ${layout}` : '';
        });
        this.shouldBeWrappedInsideAnchor = ko.pureComputed<boolean>((): boolean => {
            const hyperlink = this.hyperlink();
            const layout = this.layout();
            return hyperlink?.href && (layout.startsWith('card-slider') || (!this.videoPlayer() && !this.downloadIcon() && !(
                layout.startsWith('card-wall-abstract') ||
                layout.startsWith('card-hero') ||
                layout.startsWith('card-office') ||
                layout.startsWith('card-horizontal-editorial') ||
                layout.startsWith('card-info-slider') ||
                layout.startsWith('card-info-banner-default') ||
                layout.startsWith('card-serp-default')))
            );
        });
/*
        this.unwrappedMediaBackgroundUrl = ko.computed<string>((): string => {
            if (this.mediaBackground()?.sourceUrl) {
                const match: RegExpExecArray = /url\("(.*)"\)/.exec(this.mediaBackground().sourceUrl);
                if (match.length > 0) {
                    return match[1];
                }
            }
            return '';
        });
*/

        this.templateName = ko.pureComputed(() => {
            let layout = this.layout();
            if (layout.startsWith('card-wall-event-')) {
                layout = 'card-wall-event-any';
            }
            return layout.replace(/-dark/, '');
        });

        this.region = ko.observable<string>();
        this.country = ko.observable<string>();
        this.city = ko.observable<string>();
        this.mainOffice = ko.observable<boolean>();
        this.addressLine1 = ko.observable<string>();
        this.addressLine2 = ko.observable<string>();
        this.phone = ko.observable<string>();
        this.fax = ko.observable<string>();
        this.officeType = ko.observable<string>();
        this.googleMapsURL = ko.observable<string>();

        this.videoPlayer = ko.observable<string>();
        this.videoPlayerHero = ko.observable<string>();
        this.downloadIcon = ko.observable<boolean>();
        this.infoLine = ko.observable<string>();
        this.mainSites = ko.observableArray<string>();
        this.coverWidth = ko.observable<number>();
        this.siteLinks = ko.observable<string>();

        this.disableLazyLoadImage = ko.observable<boolean>();
    }

    @OnMounted()
    public async initialize(): Promise<void> {
        this.cmsContentKey(this.card.cmsContentKey());
        this.id(this.card.id());
        this.language(this.card.language());
        this.date(this.card.date());
        this.publicationDate(this.card.publicationDate());
        this.title(this.card.title());
        this.abstract(this.card.abstract());
        this.categoryKey(this.card.categoryKey());
        this.styles(this.card.styles());
        this.hyperlink(this.card.hyperlink());
        this.layout(this.card.layout());
        this.applyOwnGrid(this.applyOwnGrid());
        this.applyDefaultBg(this.card.applyDefaultBg());
        this.mediaBackground(this.card.mediaBackground());
        this.logoSourceUrl(this.card.logoSourceUrl());
        this.coverSourceUrl(this.card.coverSourceUrl());
        this.cta(this.card.cta());
        this.ctaAppearanceType(this.card.ctaAppearanceType());
        this.upperDate(this.card.upperDate());
        this.lowerDate(this.card.lowerDate());
        this.eventTypeKey(this.card.eventTypeKey());
        this.location(this.card.location());
        this.altDefaultBackgroundKey(this.card.altDefaultBackgroundKey());

        this.region(this.card.region());
        this.country(this.card.country());
        this.city(this.card.city());
        this.mainOffice(this.card.mainOffice());
        this.addressLine1(this.card.addressLine1());
        this.addressLine2(this.card.addressLine2());
        this.phone(this.card.phone());
        this.fax(this.card.fax());
        this.officeType(this.card.officeType());
        this.googleMapsURL(this.card.googleMapsURL());

        this.videoPlayer(this.card.videoPlayer());
        this.videoPlayerHero(this.card.videoPlayerHero());
        this.downloadIcon(this.card.downloadIcon());
        this.tags(this.card.tags());

        this.infoLine(this.card.infoLine());
        this.mainSites(this.card.mainSites());
        this.coverWidth(this.card.coverWidth());
        this.siteLinks(this.card.siteLinks());

        this.disableLazyLoadImage(this.card.disableLazyLoadImage());

        delete this.card;
    }

    private static dateToString(time: number, { locale }: { locale: Locale }): string {
        if (!time) {
            return undefined;
        }
        const date = new Date(time);
        if (Number.isNaN(date.getTime())) {
            return undefined;
        }
        return format(date, 'P', { locale });
    }

    public formatDate(dateKey: string, formatKey?: string): ko.Computed<string> {
        const locale: Locale = this.locale();
        return ko.computed<string>((): string => {
            if (dateKey === 'lower') {
                const lower: number = this.lowerDate()?.timestamp;
                return lower ? format(lower, formatKey,  { locale }) : '';
            } else if (dateKey === 'upper') {
                const upper: number = this.upperDate()?.timestamp;
                return upper ? format(upper, formatKey,  { locale }) : '';
            } else if (dateKey === 'day-interval' || dateKey === 'time-interval') {
                const lower: number = this.lowerDate()?.timestamp;
                const upper: number = this.upperDate()?.timestamp;
                if (!lower || !upper) {
                    return '';
                }
                const sameMonth: boolean = isSameMonth(lower, upper);
                const sameDay: boolean = isSameDayFunction(this.lowerDate(), this.upperDate());
                if (dateKey === 'day-interval') {
                    if (sameMonth) {
                        if (sameDay) {
                            return `${format(lower, 'dd LLL yyyy')}`;
                        } else {
                            return `${format(lower, 'dd')} - ${format(upper, 'dd')} ${format(lower, 'LLL yyyy')}`;
                        }
                    } else {
                        return `${format(lower, 'dd LLL yyyy')} - ${format(upper, 'dd LLL yyyy')}`;
                    }
                } else if (dateKey === 'time-interval' && sameDay) {
                    return `${this.lowerDate()?.time} - ${this.upperDate()?.time} ${this.lowerDate()?.timezone}`;
                }
            } else if (dateKey === 'serp-interval') {
                const lower: number = this.lowerDate()?.timestamp;
                const upper: number = this.upperDate()?.timestamp;

                if (!lower) {
                    return '';
                } else if (!upper || this.isSameDay()) {
                    return format(lower, 'dd/LL/yy');
                } else if (this.isSameMonth()) {
                    return `${format(lower, 'dd')} - ${format(upper, 'dd/LL/yy')}`;
                } else {
                    return `${format(lower, 'dd/LL/yy')} - ${format(upper, 'dd/LL/yy')}`;
                }
            }
            return 'Not supported';
        });
    }

    public static async modelToViewModel(model: ReplyCardModel, injected: ReplyCardViewModelInjectables, viewModel?: ReplyCardViewModel, bindingContext?: BindingContext): Promise<ReplyCardViewModel> {
        if (!viewModel) {
            viewModel = new ReplyCardViewModel();
        }

        viewModel.id(model.id);
        viewModel.language(bindingContext?.site?.language);
        viewModel.locale(localeSwitcher(bindingContext?.site?.language));

        viewModel.cmsContentKey(model.cmsContentKey);
        if (!model.cmsContentKey) {
            viewModel.mediaBackground(null);
        }

        let contentModel: ReplyCardModel = null;
        if (model.cmsContentKey) {
            let cmsContent: CmsContentContract;
            let callFailed = false;
            try {
                cmsContent = await injected.tamTamyCMSContentResolver.getExternalContentByKey(`tt-cms/content/${model.cmsContentKey}`, bindingContext?.locale, {
                    include: [
                        'sectionPermalink',
                        'meta.optionValues.optionValue',
                        'paperbitsPageRef',
                        'tags'
                    ]
                });
            } catch (error) {
                console.error("CMS content retrieval failed (call ended in error status). The card may contain wrong data.", error);
                callFailed = true;
            }
            if (!cmsContent) {
                if (!callFailed) {
                    console.error("CMS content retrieval failed. The card may contain wrong data.");
                }
            } else {
                contentModel = await ReplyCardModel.contentContractToModel(cmsContent, injected, bindingContext.site?.key);
            }
        }

        viewModel.date(ReplyCardViewModel.dateToString(model.date || contentModel?.date, { locale: viewModel.locale() }));
        viewModel.publicationDate(model.publicationDate || contentModel?.publicationDate);
        viewModel.title(model.title || contentModel?.title);
        viewModel.abstract(model.abstract || contentModel?.abstract);
        if (model.categoryKey === CategoryLabels.getInheritKey()) {
            viewModel.categoryKey(contentModel?.categoryKey);
        } else {
            viewModel.categoryKey(model.categoryKey);
        }
        viewModel.hyperlink(model.hyperlink || contentModel?.hyperlink);
        viewModel.layout(model.layout);
        viewModel.applyOwnGrid(model.applyOwnGrid);
        viewModel.applyDefaultBg(model.applyDefaultBg);
        viewModel.styles(null);
        viewModel.mediaBackground(null);
        viewModel.logoSourceUrl(undefined);
        viewModel.logoIcon(undefined);
        viewModel.coverSourceUrl(undefined);
        viewModel.disableLazyLoadImage(model.disableLazyLoadImage);
        viewModel.tags((contentModel?.tags || model.tags)?.slice(0, 4));
        
        if(model.logoCaption) {
            viewModel.logoCaption(model.logoCaption);
        }
        if (!model.applyDefaultBg) {
            const styles: StyleModel = await injected.backgroundProvider?.provide(model.styles, bindingContext?.styleManager);
            if (styles) {
               viewModel.styles(styles);
            } else {
                viewModel.mediaBackground(model.mediaBackground ||  contentModel?.mediaBackground);
            }
            if (model.coverSourceKey) {
                viewModel.coverSourceUrl(await injected.mediaPermalinkResolver.getAspectRatioUrlsByTargetKey(model.coverSourceKey));
            } else if (model.coverMedia || contentModel?.coverMedia) {
                const coverMedia: DamMediaContract = model.coverMedia || contentModel?.coverMedia;
                let coverWidth = model?.coverWidth || contentModel?.coverWidth || 660;
                if (model.layout?.startsWith('card-hero')) {
                    coverWidth = 1660;
                }
                const aspectRatios: AspectRatioMap = emptyAspectRatioMap();
                aspectRatios[AspectRatio.SixteenByNine] = await injected.mediaPermalinkResolver.getUrlFromMediaContract(coverMedia, { aspectRatio: AspectRatio.SixteenByNine, width: coverWidth });
                aspectRatios[AspectRatio.OneByOne] = await injected.mediaPermalinkResolver.getUrlFromMediaContract(coverMedia, { aspectRatio: AspectRatio.OneByOne, width: coverWidth });
                aspectRatios[AspectRatio.TwoByThree] = await injected.mediaPermalinkResolver.getUrlFromMediaContract(coverMedia, { aspectRatio: AspectRatio.TwoByThree, width: 660 });
                viewModel.coverSourceUrl(aspectRatios);
            }
        }

        if (model.logoIconKey) {
            const segments = model.logoIconKey.split('/');
            const name = segments[1];
            viewModel.logoIcon(`icon icon-${camelCaseToKebabCase(name.replace('/', '-'))}`);
        } else {
            viewModel.logoIcon(null);
            if (model.logoSourceKey) {
                viewModel.logoSourceUrl(await injected.mediaPermalinkResolver.getUrlByTargetKey(model.logoSourceKey, bindingContext, { aspectRatio: AspectRatio.SixteenByNine }));
            } else if (model.logoMedia || contentModel?.logoMedia) {
                const logoMedia: DamMediaContract = model.logoMedia || contentModel?.logoMedia;
                viewModel.logoSourceUrl(await injected.mediaPermalinkResolver.getUrlFromMediaContract(logoMedia, { aspectRatio: AspectRatio.SixteenByNine }));
            }
        }

        viewModel.videoPlayer(contentModel?.videoPlayer || model.videoPlayer);
        viewModel.videoPlayerHero(contentModel?.videoPlayerHero || model.videoPlayerHero);

        const ctaViewModel: Button = new Button();
        ctaViewModel.hyperlink(model.ctaHyperlink || contentModel?.ctaHyperlink);
        ctaViewModel.label(model.ctaLabel || contentModel?.ctaLabel);
        let appearanceType: string  = model.ctaType || contentModel?.ctaType;

        if (appearanceType === 'none' || !ctaViewModel.label()) {
            viewModel.cta(undefined);
            viewModel.ctaAppearanceType(undefined);
        } else if (ctaViewModel.hyperlink()) {
            const button: ButtonModel = new ButtonModel();
            const appearanceClassName = (): string => {
                const tokens: string[] = button.styles.appearance.split('/');
                if (tokens.length > 1) {
                    return tokens[tokens.length - 2];
                } else {
                    return tokens[0];
                }
            };

            switch (model.ctaType || contentModel?.ctaType) {
                case 'button': {
                    button.styles = { appearance: 'components/button/1cehk' };
                    appearanceType = 'button';
                    break;
                }
                case 'contact': {
                    button.iconKey = 'icons/ylORU';
                    button.styles = { appearance: 'components/button/link' };
                    appearanceType = 'link';
                    break;
                }
                case 'download': {
                    button.iconKey = 'icons/cSNL4';
                    button.styles = { appearance: 'components/button/link' };
                    appearanceType = 'link';
                    break;
                }
                case 'link': {
                    button.iconKey = 'icons/CKu0X';
                    button.styles = { appearance: 'components/button/link' };
                    appearanceType = 'link';
                    break;
                }
                default: {
                    button.styles = { appearance: 'components/button/link' };
                    appearanceType = 'link';
                }
            }
            if (button.iconKey) {
                const segments: string[] = button.iconKey.split('/');
                ctaViewModel.icon(`icon icon-${camelCaseToKebabCase(segments[1].replace('/', '-'))}`);
            }
            if (button.styles) {
                // Faking style compiler since it will not be available at runtime and we just need CSS classes
                const baseClassName: string = appearanceClassName();
                let classNames: string = `${baseClassName} ${baseClassName}-${button.styles.appearance.substring(button.styles.appearance.lastIndexOf('/') + 1)}`;
                if (appearanceType === 'button') {
                    classNames += ' w-full text-center';
                }
                const styleModel: StyleModel = { key: undefined, classNames, styleSheet: null };
                ctaViewModel.styles(styleModel);
            }

            viewModel.cta(ctaViewModel);
            viewModel.ctaAppearanceType(appearanceType);

        } else if (model.layout === 'card-hero-default' && viewModel.videoPlayer() && appearanceType === 'button') {
            viewModel.cta(ctaViewModel);
            viewModel.ctaAppearanceType(appearanceType);

        } else {
            viewModel.cta(undefined);
            viewModel.ctaAppearanceType(undefined);
        }

        // TODO Should the event-related part of the view model be populated if and only is the layout has the only value that right now leads to the use of the event-related variables?
        // if ( model.layout === 'card-wall-event' ) {
            const lower = model.lowerDate || contentModel?.lowerDate;
            viewModel.lowerDate(new EventDate(new Date(lower), viewModel.locale(), model?.timezone));
            const upper = model.upperDate || contentModel?.upperDate;
            viewModel.upperDate(new EventDate(new Date(upper), viewModel.locale(), model?.timezone));
            if (!model.eventType || model.eventType === EventTypes.getEmptyKey()) {
                viewModel.eventTypeKey(contentModel?.eventType);
            } else {
                viewModel.eventTypeKey(model.eventType);
            }
            viewModel.location(model.location || contentModel?.location);
            if (model.applyDefaultBg) {
                viewModel.altDefaultBackgroundKey(model.altDefaultBackground);
            } else if (!viewModel.styles() && contentModel?.applyDefaultBg) {
                viewModel.applyDefaultBg(true);
                viewModel.altDefaultBackgroundKey(contentModel?.altDefaultBackground);
            } else {
                viewModel.altDefaultBackgroundKey(undefined);
            }
        // }

        viewModel.region(contentModel?.region || model.region);

        viewModel.country(contentModel?.country || model.country);

        viewModel.city(contentModel?.city || model.city);

        viewModel.mainOffice(contentModel?.mainOffice || model.mainOffice);

        viewModel.addressLine1(contentModel?.addressLine1 || model.addressLine1);

        viewModel.addressLine2(contentModel?.addressLine2 || model.addressLine2);

        viewModel.phone(contentModel?.phone || model.phone);

        viewModel.fax(contentModel?.fax || model.fax);

        viewModel.officeType(contentModel?.officeType || model.officeType);

        viewModel.googleMapsURL(contentModel?.googleMapsURL || model.googleMapsURL);

        viewModel.downloadIcon(contentModel?.downloadIcon || model.downloadIcon);

        viewModel.infoLine(contentModel?.infoLine || model.infoLine);

        if (contentModel?.mainSites) {
            viewModel.mainSites(contentModel.mainSites);
        } else if (model.mainSites) {
            viewModel.mainSites(model.mainSites);
        }

        viewModel.siteLinks(contentModel?.siteLinks || model.siteLinks);

        return viewModel;
    }

    public t(key: string, observableValue?: ko.Observable<string>): ko.PureComputed<string> {
        return ko.pureComputed(() => `${i18nextko.t(key)()} ${observableValue()}`);
    }
}
